import React, { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Panel, Badge } from "@/components/Bits";
import { Activity, Plus, Pencil, Trash2, ExternalLink, RefreshCw, X, Save } from "lucide-react";

const ipt = "w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/40";

export default function Monitoring() {
  const { user } = useAuth();
  const canManage = ["admin", "supervisor"].includes(user?.role) || (user?.permissions || []).includes("agent_reports.manage");
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null); // panel object or {} for new

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/monitoring/panels");
      setPanels(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (p) => {
    if (!confirm(`Hapus panel "${p.name}"?`)) return;
    try {
      await api.delete(`/monitoring/panels/${p.id}`);
      load();
    } catch (e) { alert(formatErr(e)); }
  };

  return (
    <div className="p-6 space-y-4 animate-slide-up" data-testid="monitoring-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan mb-1">Live Metrics</div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Activity className="w-6 h-6 text-cyan" /> Monitoring
          </h1>
          <p className="text-xs text-slate-400 mt-1 max-w-3xl">
            Embed real-time Grafana panels untuk memantau QPS per merchant, latency, error rate, dan kestabilan sistem.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="refresh-btn"
            onClick={load}
            disabled={loading}
            className="border border-line hover:bg-surface2 px-3 py-2 rounded-sm text-xs uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-cyan" : ""}`} /> Refresh
          </button>
          {canManage && (
            <button
              data-testid="add-panel-btn"
              onClick={() => setEditing({ name: "", embed_url: "", height: 300, span: 6, order: panels.length, description: "" })}
              className="bg-cyan hover:bg-cyan-dark text-bg font-semibold px-4 py-2 rounded-sm text-sm flex items-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" /> Add Panel
            </button>
          )}
        </div>
      </div>

      {err && <div data-testid="monitoring-err" className="px-3 py-2 border border-rose-500/30 bg-rose-500/10 text-rose-400 text-xs rounded-sm">{err}</div>}

      {!loading && panels.length === 0 && (
        <Panel title="No panels yet" testid="empty-panel">
          <div className="text-sm text-slate-400 text-center py-10" data-testid="empty-state">
            <Activity className="w-8 h-8 mx-auto mb-3 text-slate-600" />
            Belum ada panel monitoring.
            {canManage ? " Klik \"Add Panel\" untuk embed link Grafana." : " Hubungi admin/supervisor untuk konfigurasi."}
          </div>
        </Panel>
      )}

      {panels.length > 0 && (
        <div className="grid grid-cols-12 gap-4" data-testid="panels-grid">
          {panels.map((p) => (
            <PanelCard key={p.id} panel={p} canManage={canManage} onEdit={() => setEditing(p)} onDelete={() => del(p)} />
          ))}
        </div>
      )}

      {editing && (
        <PanelEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function PanelCard({ panel, canManage, onEdit, onDelete }) {
  const colSpan = { 4: "col-span-12 md:col-span-4", 6: "col-span-12 md:col-span-6", 12: "col-span-12" }[panel.span] || "col-span-12 md:col-span-6";
  return (
    <div className={`${colSpan} bg-surface border border-line rounded-sm overflow-hidden flex flex-col`} data-testid={`panel-card-${panel.id}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-line bg-surface2">
        <div className="flex items-center gap-2 min-w-0">
          <Activity className="w-3.5 h-3.5 text-cyan flex-shrink-0" />
          <span className="text-sm font-semibold truncate" data-testid={`panel-name-${panel.id}`}>{panel.name}</span>
          {panel.description && <Badge color="muted">{panel.description}</Badge>}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <a href={panel.embed_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-cyan p-1" title="Buka di tab baru">
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
          {canManage && (
            <>
              <button data-testid={`edit-panel-${panel.id}`} onClick={onEdit} className="text-slate-400 hover:text-cyan p-1" title="Edit">
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button data-testid={`delete-panel-${panel.id}`} onClick={onDelete} className="text-slate-400 hover:text-rose-400 p-1" title="Delete">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
      <div className="bg-bg" style={{ height: `${panel.height}px` }}>
        <iframe
          src={panel.embed_url}
          title={panel.name}
          className="w-full h-full border-0"
          data-testid={`iframe-${panel.id}`}
          allow="fullscreen"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        />
      </div>
    </div>
  );
}

function PanelEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({ ...initial });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isNew = !form.id;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = {
        name: form.name,
        embed_url: form.embed_url,
        height: Number(form.height) || 300,
        span: Number(form.span) || 6,
        order: Number(form.order) || 0,
        description: form.description || "",
      };
      if (isNew) await api.post("/monitoring/panels", body);
      else await api.put(`/monitoring/panels/${form.id}`, body);
      onSaved();
    } catch (e) {
      setErr(formatErr(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="panel-editor">
      <div className="bg-surface border border-line rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <h3 className="text-sm font-semibold">{isNew ? "Add Grafana Panel" : "Edit Panel"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="close-editor"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          {err && <div className="px-3 py-2 border border-rose-500/30 bg-rose-500/10 text-rose-400 text-xs rounded-sm" data-testid="editor-err">{err}</div>}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Panel Name *</label>
            <input data-testid="edit-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className={ipt} placeholder="e.g. Merchant QPS" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">
              Embed URL * <span className="text-slate-500 normal-case">(Grafana d-solo iframe src)</span>
            </label>
            <textarea
              data-testid="edit-url"
              value={form.embed_url || ""}
              onChange={(e) => set("embed_url", e.target.value)}
              rows={3}
              className={`${ipt} font-mono text-[11px]`}
              placeholder="https://grafana.easylink.id/d-solo/xxx/dashboard?orgId=1&panelId=1&theme=dark&refresh=5s"
            />
            <div className="text-[10px] text-slate-500 mt-1">
              Buka Grafana → panel menu → Share → Embed → copy iframe src URL saja.
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Height (px)</label>
              <input data-testid="edit-height" type="number" min={150} max={1200} value={form.height || 300} onChange={(e) => set("height", e.target.value)} className={ipt} />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Width</label>
              <select data-testid="edit-span" value={form.span || 6} onChange={(e) => set("span", Number(e.target.value))} className={ipt}>
                <option value={4}>1/3 width</option>
                <option value={6}>Half width</option>
                <option value={12}>Full width</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Order</label>
              <input data-testid="edit-order" type="number" value={form.order ?? 0} onChange={(e) => set("order", e.target.value)} className={ipt} />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Description (optional)</label>
            <input data-testid="edit-desc" value={form.description || ""} onChange={(e) => set("description", e.target.value)} className={ipt} placeholder="Short label shown next to panel name" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-line bg-surface2">
          <button onClick={onClose} className="border border-line hover:bg-bg px-4 py-2 rounded-sm text-xs">Cancel</button>
          <button
            data-testid="save-panel-btn"
            onClick={save}
            disabled={busy || !form.name || !form.embed_url}
            className="bg-cyan text-bg font-semibold px-4 py-2 rounded-sm text-xs flex items-center gap-1.5 hover:bg-cyan-dark disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" /> {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
